/**
 * Recorder styles using MUI theme for colors.
 * Single grouped object for consistent import pattern.
 */

import { SxProps, Theme } from '@mui/material';
import {
  MENU_WIDTH_PX,
  MENU_Z_INDEX,
  TOOLTIP_Z_INDEX,
  WAVEFORM,
} from '../../../constants/sizing';

export { MENU_WIDTH_PX, MENU_Z_INDEX, TOOLTIP_Z_INDEX };

export const recorderStyles = {
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    px: { xs: 0.75, sm: 1 },
    py: 0.1,
    zIndex: 1000,
    backdropFilter: 'none',
    filter: 'none',
    bgcolor: 'custom.backgroundAlt',
    borderBottom: 1,
    borderColor: 'divider',
    p: 0,
    pb: 0.75,
    m: 0,
  } as SxProps,
  twoColumn: {
    display: 'flex',
    width: '100%',
    px: '16px',
    py: '12px',
    gap: '16px',
    alignItems: 'flex-start',
    minHeight: 'auto',
    transition: 'all 0.3s ease-in-out',
    overflow: 'visible',
  } as SxProps,
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    overflow: 'visible',
  } as SxProps,
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-end',
    minWidth: 'auto',
  } as SxProps,

  patientRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    overflow: 'visible',
    minWidth: 0,
  } as SxProps,
  patientIcon: {
    fontSize: 36,
    width: 36,
    height: 36,
    color: 'text.secondary',
  } as SxProps,
  patientInfoStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    overflow: 'visible',
    minWidth: 0,
  } as SxProps,
  getPatientName: (hasPatient: boolean): SxProps => ({
    fontSize: '16px',
    fontWeight: 'bold',
    fontStyle: hasPatient ? 'normal' : 'italic',
    color: hasPatient ? 'text.primary' : 'text.disabled',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: 2,
    minWidth: '200px',
    maxWidth: '70%',
  }),

  statusPill: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '16px',
    width: 'fit-content',
    marginTop: '2px',
  } as SxProps,
  statusText: {
    fontSize: '12px',
    fontWeight: 500,
  } as SxProps,

  durationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
  } as SxProps,
  durationSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  } as SxProps,
  durationText: {
    color: 'text.primary',
    fontSize: '14px',
    fontWeight: 500,
  } as SxProps,
  waveformContainer: {
    width: '120px',
    height: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 0,
  } as SxProps,
  waveformVisualizer: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    height: WAVEFORM.height,
    justifyContent: 'center',
    width: '100%',
    overflow: 'hidden',
    margin: 0,
    padding: 0,
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  } as SxProps,
  getWaveformBar: (theme: Theme, isTranscribing: boolean): SxProps => ({
    width: WAVEFORM.barWidth,
    backgroundColor: isTranscribing
      ? theme.palette.custom.status.transcribing.text
      : theme.palette.custom.audioWave,
    borderRadius: 0.5,
    transition: 'height 0.1s ease-out',
    opacity: 1,
  }),

  transcribeButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    marginTop: '12px',
    height: '50px',
    minHeight: '48px',
    maxHeight: '48px',
  } as SxProps,
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '50px',
    width: '100%',
    transition: 'all 0.3s ease-in-out',
    opacity: 1,
  } as SxProps,
  recordingModeGroup: {
    height: '40px',
    '& .MuiButton-root': {
      minWidth: 0,
    },
    '& .MuiButtonGroup-firstButton': {
      borderRight: '1px solid rgba(255, 255, 255, 0.55)',
    },
    '& .MuiButtonGroup-lastButton': {
      borderLeft: 'none',
    },
  } as SxProps,
  recordingModePrimaryButton: {
    flex: 1,
    minWidth: '160px !important',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  } as SxProps,
  recordingModeToggleButton: {
    minWidth: '42px !important',
    width: '42px',
    padding: '6px 8px',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  } as SxProps,
  dualButtonContainer: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  } as SxProps,
  settingsContainer: {
    alignSelf: 'flex-end',
  } as SxProps,

  tooltipPreLine: {
    whiteSpace: 'pre-line',
  } as SxProps,
  tooltipPopper: {
    zIndex: 999999,
  } as SxProps,
  warningAlert: {
    margin: '12px 0',
    borderRadius: '8px',
    '& .MuiAlert-message': {
      lineHeight: '1.5',
    },
  } as SxProps,
  telehealthErrorAlert: {
    width: 'calc(100% - 24px)',
    margin: '0 12px 12px',
    borderRadius: '8px',
    '& .MuiAlert-message': {
      lineHeight: '1.45',
    },
  } as SxProps,
  conflictAlert: {
    margin: '12px 0',
    borderRadius: '8px',
    whiteSpace: 'pre-line',
    '& .MuiAlert-message': {
      lineHeight: '1.5',
    },
  } as SxProps,
  noAudioAlert: {
    margin: '12px 0',
    borderRadius: '8px',
    whiteSpace: 'pre-line',
    '& .MuiAlert-message': {
      lineHeight: '1.5',
    },
    // widget-core puts this class on the widget container; collapsed, the alert
    // would be clipped, so the NO AUDIO status pill carries the warning instead.
    '.widget-collapsed &': {
      display: 'none',
    },
  } as SxProps,
  resumeConfirmationActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  } as SxProps,

  menuList: { py: 0.75 } as SxProps,
  menu: {
    zIndex: MENU_Z_INDEX,
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
      backdropFilter: 'none',
    },
  } as SxProps,
  menuMainOrigins: {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' } as const,
    transformOrigin: { vertical: 'top', horizontal: 'right' } as const,
  },
  menuSubmenuOrigins: {
    anchorOrigin: { vertical: 'top', horizontal: 'right' } as const,
    transformOrigin: { vertical: 'top', horizontal: 'left' } as const,
  },

  microphoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
  } as SxProps,
  microphoneContent: {
    flex: 1,
    minWidth: 0,
  } as SxProps,
  microphonePrimaryText: {
    lineHeight: 1.1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  } as SxProps,
  microphoneSecondaryText: {
    lineHeight: 1,
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  } as SxProps,
  microphoneErrorText: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  } as SxProps,
  microphoneRetryText: {
    fontWeight: 500,
    color: 'primary.main',
  } as SxProps,
};
